import { useTranslation } from 'react-i18next';
import { BedDouble, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  STAY_SOURCE_KEY,
  formatStayDate,
  formatStayRange,
  type StayMismatchView,
  type StayPlanView,
} from '@/lib/allocation/stay';

// HOW LONG the stay is, rendered the same way everywhere (feature `stay-nights`,
// slice N8).
//
// Before this slice the console showed a per-night RATE and nothing else — no
// night count, no dates, in any surface. An operator could not tell a one-night
// stay from a four-night one, which is precisely how a US$120 room quietly became
// a US$480 booking nobody reviewed. This is the one component that answers "how
// many nights, which dates, and why that number", so the occupancy board, the
// hotel desk, the PNR drawer and the allocation plan table cannot drift apart.
//
// It always says WHERE the number came from (`source`), because the operator's
// next move depends on it: a `REBOOKING`/`MANIFEST` count is evidence from a real
// onward flight and should be trusted; a `DEFAULT` is the system admitting it has
// none; an `OPERATOR_*` one is somebody's decision that can be revisited.
//
// Copy lives in i18n under `cases:stayPlan.*`.

export interface StayLineProps {
  plan: StayPlanView | null | undefined;
  /** Drop the dates and the basis flight — for dense rows (plan table, board). */
  compact?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function StayLine({ plan, compact, className, ...rest }: StayLineProps) {
  const { t } = useTranslation('cases');
  if (!plan) return null;
  // `nights: 0` is this codebase's "a window is known but its night count is not"
  // (a caller-pinned raw window states instants, never a count — `readPlanRowStay`).
  // Show the dates and stay silent about the number rather than printing "0 nights".
  const known = plan.nights >= 1;
  const range = formatStayRange(plan.checkInUtc, plan.checkOutUtc);
  const sourceLabel = t(STAY_SOURCE_KEY[plan.source]);
  return (
    <span
      className={cn('inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5', className)}
      title={t('stayPlan.sourceTitle', { source: sourceLabel })}
      data-testid={rest['data-testid'] ?? 'stay-line'}
    >
      <BedDouble className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
      {known ? (
        <span className="font-medium tabular-nums">
          {t('stayPlan.nights', { count: plan.nights })}
        </span>
      ) : null}
      {!compact && range ? <span className="tabular-nums text-muted-foreground">{range}</span> : null}
      <span className="text-muted-foreground">{sourceLabel}</span>
      {!compact && plan.basisFlight ? (
        <span className="text-muted-foreground">
          {t('stayPlan.basis', { flight: plan.basisFlight.flightNumber })}
        </span>
      ) : null}
      {plan.cappedByPolicy ? (
        <span
          className="rounded bg-amber-100 px-1 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
          title={t('stayPlan.cappedTitle')}
        >
          {t('stayPlan.capped')}
        </span>
      ) : null}
    </span>
  );
}

export interface StayStripProps {
  plan: StayPlanView | null | undefined;
  className?: string;
}

/**
 * The PNR drawer's STAY, date-first and visual (operator finding G): the
 * from → to dates + the night count as the prominent line, then a compact
 * timeline bar with check-in / check-out labeled at the ends. Presentation
 * only — it renders the exact `StayPlanView` fields `StayLine` renders (no new
 * date arithmetic: `nights` is READ, never derived from the instants).
 */
export function StayStrip({ plan, className }: StayStripProps) {
  const { t } = useTranslation('cases');
  if (!plan) return null;
  // `nights: 0` = "window known, count not" (see StayLine) — show dates, no count.
  const known = plan.nights >= 1;
  const from = formatStayDate(plan.checkInUtc);
  const to = formatStayDate(plan.checkOutUtc);
  const sourceLabel = t(STAY_SOURCE_KEY[plan.source]);
  return (
    <div className={cn('min-w-0', className)} data-testid="stay-strip">
      <p className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
        {from && to ? (
          <span className="text-sm font-semibold tabular-nums" data-testid="stay-strip-range">
            {from} {'→'} {to}
          </span>
        ) : null}
        {known ? (
          <span className="text-sm tabular-nums text-muted-foreground" data-testid="stay-strip-nights">
            {from && to ? '· ' : ''}
            {t('stayPlan.nights', { count: plan.nights })}
          </span>
        ) : null}
        {plan.cappedByPolicy ? (
          <span
            className="rounded bg-amber-100 px-1 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
            title={t('stayPlan.cappedTitle')}
            data-testid="stay-strip-capped"
          >
            {t('stayPlan.capped')}
          </span>
        ) : null}
        <span className="text-[11px] text-muted-foreground" title={t('stayPlan.sourceTitle', { source: sourceLabel })}>
          {sourceLabel}
        </span>
        {plan.basisFlight ? (
          <span className="text-[11px] text-muted-foreground">
            {t('stayPlan.basis', { flight: plan.basisFlight.flightNumber })}
          </span>
        ) : null}
      </p>
      {from && to ? (
        <>
          {/* The compact timeline: a dot at each end of the stay, the nights
              spanning between them. Decorative — the labels below carry it. */}
          <div className="mt-1.5 flex items-center" aria-hidden>
            <span className="h-2 w-2 shrink-0 rounded-full border-2 border-foreground/60 bg-background" />
            <span className="h-px flex-1 border-t border-dashed border-foreground/40" />
            <BedDouble className="mx-1 h-3 w-3 shrink-0 text-muted-foreground" />
            <span className="h-px flex-1 border-t border-dashed border-foreground/40" />
            <span className="h-2 w-2 shrink-0 rounded-full border-2 border-foreground/60 bg-foreground/60" />
          </div>
          <div className="mt-0.5 flex items-baseline justify-between gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
            <span data-testid="stay-strip-checkin">
              {t('stayPlan.checkIn')} {from}
            </span>
            <span data-testid="stay-strip-checkout">
              {t('stayPlan.checkOut')} {to}
            </span>
          </div>
        </>
      ) : null}
    </div>
  );
}

export interface StayMismatchNoteProps {
  mismatch: StayMismatchView | null | undefined;
  /** Pin the booked count / the derived count as this PNR's number, ending the
   *  conflict. Absent ⇒ the note is informational (no disruption to write to). */
  onResolve?: ((nights: number) => void) | undefined;
  pending?: boolean;
  className?: string;
}

/**
 * A night count moved while a hotel was already at stake (§6.5). This is never a
 * toast: money is committed, the plan and the reservation disagree, and the
 * disagreement stands until a human picks a side. `AUTO_ADJUSTED` means policy
 * already picked one and this is the audit line, so no buttons are offered.
 *
 * Resolving is a per-PNR write of the winning number (`OPERATOR_GROUP`, the top
 * rung) — whichever the operator picks, the group stops being re-derived out from
 * under its booking.
 */
export function StayMismatchNote({ mismatch, onResolve, pending, className }: StayMismatchNoteProps) {
  const { t } = useTranslation('cases');
  if (!mismatch) return null;
  const auto = mismatch.resolution === 'AUTO_ADJUSTED';
  return (
    <div
      className={cn(
        'rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200',
        className,
      )}
      data-testid="stay-mismatch-note"
    >
      <p className="flex items-start gap-1.5">
        <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>
          {t('stayPlan.mismatch.body', {
            booked: mismatch.bookedNights,
            derived: mismatch.derivedNights,
          })}
          {mismatch.reservationStatus
            ? ` ${t('stayPlan.mismatch.atStake', { status: mismatch.reservationStatus })}`
            : ''}
          {auto ? ` ${t('stayPlan.mismatch.autoAdjusted')}` : ''}
        </span>
      </p>
      {!auto && onResolve ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={pending}
            onClick={() => onResolve(mismatch.bookedNights)}
            className="rounded border border-amber-400 px-2 py-0.5 font-medium hover:bg-amber-100 disabled:opacity-50 dark:border-amber-700 dark:hover:bg-amber-900/40"
            data-testid="stay-mismatch-keep"
          >
            {t('stayPlan.mismatch.keepBooked', { count: mismatch.bookedNights })}
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={() => onResolve(mismatch.derivedNights)}
            className="rounded border border-amber-400 px-2 py-0.5 font-medium hover:bg-amber-100 disabled:opacity-50 dark:border-amber-700 dark:hover:bg-amber-900/40"
            data-testid="stay-mismatch-use-derived"
          >
            {t('stayPlan.mismatch.useDerived', { count: mismatch.derivedNights })}
          </button>
        </div>
      ) : null}
    </div>
  );
}
